import React, { memo, useMemo, useState } from 'react'

import { FiltersSidebar, Button } from '@cgtrader/triangle-ui/dist/src/ssr-index'
import { arrayOf, bool, func, number, object, string } from 'prop-types'

import FilterSearchInput from 'Pods/SearchPage/Components/Shared/FilterSearchInput/FilterSearchInput'
import useTranslate from 'Pods/SearchPage/Shared/useTranslate'
import { useSearchPageStore } from 'Pods/SearchPage/Store/index'

const cleanedFiltersLabels = (label) => label
  .replace(/,\s*,/g, ',')
  .replace(/,\s*\)/g, ')')
  .replace(/\(\s*,/g, '(')
  .replace(/,\s*,\s*/g, ', ')

// "Blender (.blend)" -> name + parenthesised extensions.
const EXTENSION_PATTERN = /^(.*?)\s*(\([^)]*\))$/

const renderLabel = (label) => {
  const match = label.match(EXTENSION_PATTERN)
  if (!match) return label

  return (
    <span className="file-format-filter__label">
      <span>{match[1]}</span>
      {/* Keeps the accessible name "Blender (.blend)"; the flex gap renders the visual spacing. */}
      {' '}
      <span className="file-format-filter__extension">{match[2]}</span>
    </span>
  )
}

function FileFormatContent({
  fileTypes, onFilterChange, searchRef = null, limit = 8, hideShowMore = true,
}) {
  const translate = useTranslate()

  // The same backend list (ListingPageFiltersHelper#file_types, ordered by priority) the legacy
  // search page renders, so both dropdowns open on the same formats.
  const fileTypeOptions = useSearchPageStore(state => state.filtersOptions?.fileTypes)
  const cleanedFilters = useMemo(() => (fileTypeOptions?.filters ?? []).map(filter => ({
    ...filter,
    label: cleanedFiltersLabels(filter.label),
  })), [fileTypeOptions])

  const [query, setQuery] = useState('')
  // The drawer unmounts this on close, so a format past `limit` would come back hidden behind
  // "Show more" with nothing saying it is on. Start expanded when one is, and only on mount -
  // re-deriving would refuse to collapse while such a format is applied.
  const [showAll, setShowAll] = useState(() => !hideShowMore && cleanedFilters
    .slice(limit)
    .some(filter => fileTypes.includes(filter.value)))

  // Search lives here instead of FiltersSidebar's built-in one: that lowercases each `label`,
  // so it can only match plain strings, while the rendered label is a node with a muted extension.
  const filtersConfig = useMemo(() => {
    const normalized = query.trim().toLowerCase()
    const matching = normalized
      ? cleanedFilters.filter(filter => filter.label.toLowerCase().includes(normalized))
      : cleanedFilters

    return [{
      ...fileTypeOptions,
      label: null,
      isSearchable: false,
      filters: matching.map(filter => ({ ...filter, label: renderLabel(filter.label) })),
    }]
  }, [query, cleanedFilters, fileTypeOptions])

  // limit=0 tells FiltersGroup to show all items (0 || initFilters.length = all)
  const effectiveLimit = !hideShowMore && showAll ? 0 : limit

  return (
    <div className="file-format-filter__content">
      <FilterSearchInput
        id="file-format-search"
        value={query}
        onChange={setQuery}
        placeholder={translate('search_page.filters.file_format.search_placeholder')}
        ariaLabel={translate('search_page.filters.file_format.title')}
        inputRef={searchRef}
      />
      <FiltersSidebar
        isCollapsible={false}
        filters={filtersConfig}
        selectedFilters={fileTypes}
        onFiltersChange={onFilterChange}
        limit={effectiveLimit}
        isControlled
        hideLastDivider
        useNewCheckboxes
        hideShowMore
        baseFormWrapperClassName="filter-wrapper__content"
      />
      {!hideShowMore && !showAll && (
        <Button
          type="ghost-primary"
          size="small"
          customClass="filter-wrapper__show-more cgt-link-primary"
          onClick={() => setShowAll(true)}
        >
          {translate('search_page.filters.file_format.show_more')}
        </Button>
      )}
      {!hideShowMore && showAll && (
        <Button
          type="ghost-primary"
          size="small"
          customClass="filter-wrapper__show-more cgt-link-primary"
          onClick={() => setShowAll(false)}
        >
          {translate('search_page.filters.file_format.show_less')}
        </Button>
      )}
    </div>
  )
}

FileFormatContent.propTypes = {
  fileTypes: arrayOf(string).isRequired,
  onFilterChange: func.isRequired,
  searchRef: object,
  limit: number,
  hideShowMore: bool,
}

export default memo(FileFormatContent)
